/**
 * The Leads tab: count strip (clickable filters), filter chips, search, one list sorted so leads that need the owner sit on top.
 * Rows open the lead panel. "Chats without contact" lists website chats where the visitor left no email.
 */
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { AlertCircle, CalendarCheck, ChevronLeft, ChevronRight, Loader2, MessageSquare, Search, Sparkles, UserCheck, Inbox, Ban, PhoneCall } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  FIT_LABEL, FIT_STYLE, STAGE_LABEL, STAGE_STYLE, getJson, leadsKey, relTime, dateTime, sendJson, telHref,
  type ChatsResponse, type LeadListResponse, type LeadRow, type ListFilter, type OpenTask,
} from "./api";

type ChipFilter = ListFilter | "chats";
const CHIPS: Array<{ id: ChipFilter; label: string; count?: (c: LeadListResponse["counts"]) => number }> = [
  { id: "needs_you", label: "Needs you", count: (c) => c.needsYou },
  { id: "new", label: "New", count: (c) => c.byStage.new },
  { id: "booked", label: "Booked", count: (c) => c.byStage.booked },
  { id: "clients", label: "Clients", count: (c) => c.byStage.client },
  { id: "lost", label: "Lost", count: (c) => c.byStage.lost },
  { id: "all", label: "All", count: (c) => c.byStage.all },
  { id: "chats", label: "Chats without contact", count: (c) => c.chatsWithoutContact },
];
const STRIP_LABEL: Partial<Record<ListFilter, string>> = { new_today: "New today", booked_week: "Booked this week", clients_month: "Became clients this month" };
const PAGE_SIZE = 50;

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export default function LeadsTab({ workspaceId, onOpenLead }: { workspaceId: number; onOpenLead: (id: number) => void }) {
  const [filter, setFilter] = useState<ChipFilter>("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const dq = useDebounced(q, 250);
  useEffect(() => { setPage(1); }, [filter, dq]);

  const listFilter: ListFilter = filter === "chats" ? "all" : filter;
  const { data, isLoading, isError, error, isFetching } = useQuery<LeadListResponse>({
    queryKey: [...leadsKey(workspaceId), "list", listFilter, dq, page],
    queryFn: () => getJson(`/api/workspaces/${workspaceId}/lead-desk/leads?filter=${listFilter}&q=${encodeURIComponent(dq)}&page=${page}&pageSize=${PAGE_SIZE}`),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
  const counts = data?.counts;

  const strip: Array<{ id: ListFilter; label: string; value: number | undefined; icon: any; tone: string }> = [
    { id: "new_today", label: "New today", value: counts?.newToday, icon: Sparkles, tone: "text-blue-400" },
    { id: "needs_you", label: "Needs you", value: counts?.needsYou, icon: AlertCircle, tone: "text-amber-400" },
    { id: "booked_week", label: "Booked this week", value: counts?.bookedThisWeek, icon: CalendarCheck, tone: "text-violet-400" },
    { id: "clients_month", label: "Became clients this month", value: counts?.clientsThisMonth, icon: UserCheck, tone: "text-emerald-400" },
  ];

  return (
    <div className="space-y-5" data-testid="leads-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="lead-count-strip">
        {strip.map((s) => (
          <button
            key={s.id}
            type="button"
            data-testid={`count-${s.id}`}
            onClick={() => setFilter(filter === s.id ? "all" : s.id)}
            className={cn(
              "text-left rounded-2xl border bg-card/50 px-4 py-3 transition-colors hover:bg-muted/40",
              filter === s.id ? "border-blue-500/50 ring-1 ring-blue-500/30" : "border-border",
            )}
          >
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <s.icon size={14} className={s.tone} />
              <span className="truncate">{s.label}</span>
            </div>
            <div className="mt-1 text-2xl font-display font-bold text-foreground" data-testid={`count-value-${s.id}`}>
              {s.value ?? (isLoading ? "…" : 0)}
            </div>
          </button>
        ))}
      </div>

      {!!data?.unassignedTasks?.length && <UnassignedTasks workspaceId={workspaceId} tasks={data.unassignedTasks} />}

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="lead-filter-chips">
          {CHIPS.map((c) => {
            const n = counts && c.count ? c.count(counts) : undefined;
            const active = filter === c.id;
            return (
              <button
                key={c.id}
                type="button"
                data-testid={`chip-${c.id}`}
                onClick={() => setFilter(c.id)}
                className={cn(
                  "shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                  active ? "border-blue-500/40 bg-blue-500/15 text-blue-300" : "border-border bg-muted/30 text-muted-foreground hover:text-foreground",
                )}
              >
                {c.id === "chats" && <MessageSquare size={12} />}
                {c.label}
                {n !== undefined && <span className={cn("rounded-full px-1.5 text-[10px]", active ? "bg-blue-500/20" : "bg-muted")}>{n}</span>}
              </button>
            );
          })}
        </div>
        {filter !== "chats" && (
          <div className="relative md:w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="input-lead-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or email"
              className="pl-8"
            />
          </div>
        )}
      </div>

      {STRIP_LABEL[filter as ListFilter] && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="active-strip-filter">
          Showing: <span className="text-foreground font-medium">{STRIP_LABEL[filter as ListFilter]}</span>
          <button type="button" className="text-blue-400 hover:underline" onClick={() => setFilter("all")} data-testid="button-clear-strip-filter">Show all</button>
        </div>
      )}

      {filter === "chats" ? (
        <ChatsWithoutContact workspaceId={workspaceId} />
      ) : isError ? (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm" data-testid="leads-error">
          <p className="font-semibold text-red-400">Couldn't load leads</p>
          <p className="text-muted-foreground mt-1">{(error as Error)?.message}</p>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-muted-foreground" /></div>
      ) : !data || data.rows.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card/50 p-10 text-center" data-testid="leads-empty">
          <Inbox size={32} className="mx-auto text-muted-foreground/40 mb-2" />
          <p className="text-sm text-muted-foreground">{dq ? "No leads match that search." : filter === "needs_you" ? "Nothing needs you right now." : "No leads here yet."}</p>
        </div>
      ) : (
        <>
          <LeadTable rows={data.rows} onOpen={onOpenLead} />
          <LeadCards rows={data.rows} onOpen={onOpenLead} />
          <Pager page={data.page} pageSize={data.pageSize} total={data.total} busy={isFetching} onPage={setPage} />
        </>
      )}
    </div>
  );
}

function StageBadge({ row }: { row: Pick<LeadRow, "stage" | "optedOut"> }) {
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      <span className={cn("inline-flex rounded-md border px-2 py-0.5 text-[11px] font-medium", STAGE_STYLE[row.stage])}>{STAGE_LABEL[row.stage]}</span>
      {row.optedOut && (
        <span className="inline-flex items-center gap-0.5 rounded-md border border-red-500/30 bg-red-500/10 px-1.5 py-0.5 text-[10px] font-medium text-red-400" title="Opted out: no marketing email">
          <Ban size={10} /> Opted out
        </span>
      )}
    </span>
  );
}

function FitText({ row }: { row: Pick<LeadRow, "fit"> }) {
  if (!row.fit) return <span className="text-muted-foreground/50">—</span>;
  return <span className={cn("text-xs font-medium", FIT_STYLE[row.fit])}>{FIT_LABEL[row.fit]}</span>;
}

function NextActionText({ row }: { row: LeadRow }) {
  const a = row.nextAction;
  if (!a) return <span className="text-muted-foreground/50">—</span>;
  const overdue = a.dueAt && Date.parse(a.dueAt) < Date.now();
  return (
    <div className="min-w-0">
      <div className={cn("text-xs truncate", row.needsYou ? "text-amber-300 font-medium" : "text-foreground/80")} title={a.label}>{a.label}</div>
      {a.dueAt && <div className={cn("text-[11px]", overdue ? "text-red-400" : "text-muted-foreground")} title={dateTime(a.dueAt)}>{overdue ? "overdue · " : ""}{relTime(a.dueAt)}</div>}
    </div>
  );
}

function LeadTable({ rows, onOpen }: { rows: LeadRow[]; onOpen: (id: number) => void }) {
  return (
    <div className="hidden md:block rounded-2xl border border-border bg-card/50 overflow-hidden" data-testid="lead-table">
      <table className="w-full text-sm table-fixed">
        <thead>
          <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-muted-foreground">
            <th className="px-4 py-2.5 font-semibold w-[22%]">Lead</th>
            <th className="px-3 py-2.5 font-semibold w-[16%]">Need</th>
            <th className="px-3 py-2.5 font-semibold w-[8%]">Fit</th>
            <th className="px-3 py-2.5 font-semibold w-[11%]">Estimate</th>
            <th className="px-3 py-2.5 font-semibold w-[12%]">Stage</th>
            <th className="px-3 py-2.5 font-semibold w-[20%]">Next action</th>
            <th className="px-4 py-2.5 font-semibold w-[11%]">Last contact</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              data-testid={`lead-row-${r.id}`}
              tabIndex={0}
              onClick={() => onOpen(r.id)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(r.id); } }}
              className={cn("border-b border-border/60 last:border-0 cursor-pointer transition-colors hover:bg-muted/40 focus:outline-none focus:bg-muted/40", r.needsYou && "bg-amber-500/[0.04]")}
            >
              <td className="px-4 py-3 align-top">
                <div className="flex items-start gap-2 min-w-0">
                  {r.needsYou && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-amber-400" title="Needs you" data-testid={`needs-you-dot-${r.id}`} />}
                  <div className="min-w-0">
                    <div className="font-medium text-foreground truncate" data-testid={`lead-name-${r.id}`}>{r.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{r.source || "—"}</div>
                  </div>
                </div>
              </td>
              <td className="px-3 py-3 align-top text-xs text-foreground/80"><div className="line-clamp-2">{r.need || <span className="text-muted-foreground/50">—</span>}</div></td>
              <td className="px-3 py-3 align-top"><FitText row={r} /></td>
              <td className="px-3 py-3 align-top text-xs text-foreground/80 truncate" title={r.estimateText || ""}>{r.estimateText || <span className="text-muted-foreground/50">—</span>}</td>
              <td className="px-3 py-3 align-top"><StageBadge row={r} /></td>
              <td className="px-3 py-3 align-top"><NextActionText row={r} /></td>
              <td className="px-4 py-3 align-top text-xs text-muted-foreground" title={dateTime(r.lastContactAt)}>{relTime(r.lastContactAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeadCards({ rows, onOpen }: { rows: LeadRow[]; onOpen: (id: number) => void }) {
  return (
    <div className="md:hidden space-y-2" data-testid="lead-cards">
      {rows.map((r) => (
        <button
          key={r.id}
          type="button"
          data-testid={`lead-card-${r.id}`}
          onClick={() => onOpen(r.id)}
          className={cn("w-full text-left rounded-2xl border bg-card/50 px-4 py-3 space-y-2 transition-colors hover:bg-muted/40", r.needsYou ? "border-amber-500/30" : "border-border")}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-medium text-foreground truncate">{r.name}</div>
              <div className="text-[11px] text-muted-foreground truncate">{r.source}{r.need ? ` · ${r.need}` : ""}</div>
            </div>
            <StageBadge row={r} />
          </div>
          <div className="flex items-center justify-between gap-3 text-xs">
            <NextActionText row={r} />
            <div className="shrink-0 text-right text-muted-foreground space-y-0.5">
              {(r.fit || r.estimateText) && <div>{r.fit && <FitText row={r} />}{r.estimateText ? <span className="ml-1.5">{r.estimateText}</span> : null}</div>}
              {r.lastContactAt && <div className="text-[11px]">{relTime(r.lastContactAt)}</div>}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function Pager({ page, pageSize, total, busy, onPage }: { page: number; pageSize: number; total: number; busy: boolean; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground" data-testid="lead-pager">
      <span>{from}–{to} of {total}{busy && <Loader2 size={12} className="inline ml-2 animate-spin" />}</span>
      {pages > 1 && (
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="button-page-prev"><ChevronLeft /> Prev</Button>
          <span className="px-2">Page {page} of {pages}</span>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="button-page-next">Next <ChevronRight /></Button>
        </div>
      )}
    </div>
  );
}

function ChatsWithoutContact({ workspaceId }: { workspaceId: number }) {
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<number | null>(null);
  const { data, isLoading, isError, error, isFetching } = useQuery<ChatsResponse>({
    queryKey: [...leadsKey(workspaceId), "chats", page],
    queryFn: () => getJson(`/api/workspaces/${workspaceId}/lead-desk/chats-without-contact?page=${page}&pageSize=25`),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-muted-foreground" /></div>;
  if (isError) return <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-400" data-testid="chats-error">{(error as Error)?.message}</div>;
  if (!data || data.rows.length === 0) {
    return (
      <div className="rounded-2xl border border-border bg-card/50 p-10 text-center" data-testid="chats-empty">
        <MessageSquare size={32} className="mx-auto text-muted-foreground/40 mb-2" />
        <p className="text-sm text-muted-foreground">No anonymous chats. Every visitor who chatted left an email.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="chats-without-contact">
      <p className="text-xs text-muted-foreground">Website chats where the visitor left no email. They are not leads yet; read what they asked to see what people want.</p>
      {data.rows.map((c) => (
        <div key={c.id} className="rounded-2xl border border-border bg-card/50" data-testid={`chat-${c.id}`}>
          <button type="button" className="w-full text-left px-4 py-3 flex items-start justify-between gap-3" onClick={() => setOpen(open === c.id ? null : c.id)} data-testid={`button-toggle-chat-${c.id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium text-foreground truncate">{c.visitorName || "Anonymous visitor"}{c.need ? <span className="text-muted-foreground font-normal"> · {c.need}</span> : null}</div>
              <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{c.preview || "—"}</div>
              {c.pageUrl && <div className="text-[11px] text-muted-foreground/70 truncate mt-0.5">{c.pageUrl}</div>}
            </div>
            <div className="shrink-0 text-right text-[11px] text-muted-foreground">
              <div>{relTime(c.lastMessageAt)}</div>
              <div>{c.messageCount} msg</div>
              {c.visitorPhone && <a href={`tel:${c.visitorPhone}`} onClick={(e) => e.stopPropagation()} className="text-blue-400 hover:underline" data-testid={`link-chat-phone-${c.id}`}>{c.visitorPhone}</a>}
            </div>
          </button>
          {open === c.id && (
            <div className="border-t border-border px-4 py-3 space-y-2 max-h-96 overflow-y-auto" data-testid={`chat-transcript-${c.id}`}>
              {c.messages.map((m, i) => (
                <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                  <div className={cn("max-w-[85%] rounded-xl px-3 py-2 text-xs whitespace-pre-wrap", m.role === "user" ? "bg-blue-500/10 text-foreground" : "bg-muted/50 text-foreground/80")}>{m.text}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      <Pager page={data.page} pageSize={data.pageSize} total={data.total} busy={isFetching} onPage={setPage} />
    </div>
  );
}


/** Callbacks and hand-offs from visitors who left only a phone number: no lead row exists, but they still need the owner. */
function UnassignedTasks({ workspaceId, tasks }: { workspaceId: number; tasks: OpenTask[] }) {
  const qc = useQueryClient();
  const close = useMutation({
    mutationFn: ({ id, status }: { id: number; status: "done" | "dismissed" }) => sendJson("POST", `/api/workspaces/${workspaceId}/lead-tasks/${id}/close`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: leadsKey(workspaceId) }),
  });
  return (
    <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-2" data-testid="unassigned-tasks">
      <div className="text-xs font-semibold uppercase tracking-wider text-amber-300">Needs you, no contact details</div>
      {tasks.map((t) => {
        const phone = typeof t.payload?.phone === "string" ? t.payload.phone : null;
        return (
          <div key={t.id} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between" data-testid={`unassigned-task-${t.id}`}>
            <div className="min-w-0">
              <div className="text-sm text-foreground truncate">{t.title}</div>
              <div className="text-[11px] text-muted-foreground">{t.detail ? `${t.detail} · ` : ""}{relTime(t.createdAt)}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              {phone && (
                <Button asChild size="sm" variant="outline" data-testid={`unassigned-task-call-${t.id}`}>
                  <a href={telHref(phone)}><PhoneCall size={14} className="mr-1" />{phone}</a>
                </Button>
              )}
              <Button size="sm" variant="outline" disabled={close.isPending} onClick={() => close.mutate({ id: t.id, status: "done" })} data-testid={`unassigned-task-done-${t.id}`}>Done</Button>
              <Button size="sm" variant="ghost" disabled={close.isPending} onClick={() => close.mutate({ id: t.id, status: "dismissed" })} data-testid={`unassigned-task-dismiss-${t.id}`}>Dismiss</Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
